import React, { useState, useMemo, useRef, useEffect } from 'react'
import { Spin, Input, Pagination, Empty, Button, Checkbox, message, Tag, Tabs, Tooltip } from 'antd'
import { COLORS } from 'theme'
import { SearchOutlined } from '@ant-design/icons'
import FilterIcon from 'components/icons/FilterIcon'
import ArrowDropDown from 'components/icons/ArrowDropDown'
import FilterButtonComponent from 'components/FilterButton'
import { FilterContainer, FilterPopup } from 'components/FilterButton/styles'
import Avatar from 'components/Avatar'
import CopyLinkButton from 'components/CopyLinkButton'
import DownloadButton from 'components/DownloadButton'
import { fixedSorterColumn } from 'utils/constants'
import StudentReportModal from '../StudentReport'
import { resolveRoleBasedSectionColumns } from '../roleBasedColumns'
import { APTITUDE_SECTION_LABELS, resolveAptitudeSectionKeys } from '../aptitudeSections'
import {
  SORT_KIND,
  getProctoringLabel,
  proficiencyRank,
  proctoringRank,
  sortRows,
} from '../studentSorters'
import DeliveryStatusTag from '../DeliveryStatusTag'
import { ATTEMPT_STATUS, ATTEMPT_STATUS_TOOLTIP, toAttemptStatusKey } from '../attemptStatus'
import {
  SearchSection,
  ShowingText,
  Overlay,
  TableTop,
  CustomStyledTable,
  FilterSummary,
  ClearButton,
} from '../../Style/style'

// antd's sort order → the 'asc' / 'desc' vocabulary fixedSorterColumn expects.
// An unsorted column maps to undefined, which yields "Click to sort ascending".
const SORTER_TOOLTIP_ORDER = { ascend: 'asc', descend: 'desc' }

// Proctoring outcome → text colour. Keyed by the labels getProctoringLabel emits.
const PROCTORING_COLORS = {
  Good: '#0FB680',
  Bad: '#E35E50',
  Evaluating: '#F59E0B',
}

/** Every assessment type reports out of 100, so one formatter serves them all. */
const formatPartScore = value =>
  typeof value === 'number' ? `${Math.round(value * 100) / 100}%` : '-'

const PART_TYPE_LABELS = {
  Aptitude: 'Aptitude',
  Communication: 'Communication',
  Hinglish: 'Communication',
  Role_Based: 'Role Based',
  AI_Interview: 'AI Interview',
  AI_Interview_v2: 'Complete AI Interview',
  Custom_Assessment: 'Custom',
  Behavior: 'Behaviour',
}
const partLabel = type => PART_TYPE_LABELS[type] || String(type || '').replace(/_/g, ' ')

const PART_STATUS_COLORS = {
  COMPLETED: 'green',
  INPROGRESS: 'blue',
  PENDING: 'default',
  DROPOUT: 'red',
}

const StudentsTable = ({
  loading,
  students = [],
  statusType,
  totalItems = 0,
  limit = 10,
  page = 0,
  onPageChange = () => { },
  filterValues = {},
  onClearFilters = () => { },
  assessmentId,
  exportStudentData,
  sendRemindersToStudents,
  resendInvitesToStudents,
  entityType = 'college',
  // Left undefined when the caller does not know: only an explicit `false`
  // tells the report that proctoring was switched off for this assessment.
  allowProctoring,
  assessmentType = '', // Added assessment type prop
  roleBasedSections = [], // Sections selected at creation (count > 0) — drives role-based columns
  /** The assessments a Mix & Match float is made of — one score column each. */
  mixMatchParts = [],
  onSearch,
  searchValue: propSearchValue = '',
}) => {
  const [searchValue, setSearchValue] = useState(propSearchValue)
  const [showFilters, setShowFilters] = useState(false)
  // Active sort, controlled here rather than by antd: the table only ever sees
  // the current page, so it cannot do the sorting itself (see studentSorters.js).
  const [sorter, setSorter] = useState({ key: null, order: null })

  // Sync local search value with prop
  React.useEffect(() => {
    setSearchValue(propSearchValue)
  }, [propSearchValue])
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [showReportModal, setShowReportModal] = useState(false)
  /** A float's report is its parts' reports; this is the one on screen. */
  const [activePartId, setActivePartId] = useState(null)
  const isMixMatch = String(assessmentType || '').toLowerCase() === 'mix_match'
  const [sendingReminders, setSendingReminders] = useState(false)
  const [resendingInvites, setResendingInvites] = useState(false)
  const [selectedStudentsForResend, setSelectedStudentsForResend] = useState([]) // Track selected students for resend
  const [selectedStudentsForReminder, setSelectedStudentsForReminder] = useState([]) // Track selected students for reminder

  // Schedule/assessment dates (sent/start/end) are stored as IST-wall-clock-as-UTC,
  // so render date AND time in UTC (else IST browsers add +5:30, showing the wrong
  // time and +1 day past 18:30 UTC).
  const formatDateOnly = dateString => {
    if (!dateString || dateString === '-') return ''
    // getAssessmentDetails already returns these as pre-formatted IST date strings
    // (e.g. "13 Jul 2026"). Render them as-is — re-parsing a bare date string via
    // new Date()+UTC shifted the value -5:30 and showed a bogus previous-day
    // 6:30 PM. Only genuine ISO timestamps get UTC date formatting.
    const isIso = /\d{4}-\d{2}-\d{2}T/.test(dateString)
    if (!isIso) return dateString
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
  }

  // Real event timestamps (started / dropped-off) are genuine UTC instants — render
  // in the browser's local (IST) zone so the shown time reflects when it happened.
  const formatEventDateOnly = dateString => {
    if (!dateString) return ''
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return ''
    }
    const datePart = date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
    const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    return `${datePart} ${timePart}`
  }

  // In-progress rows can carry the start instant under any of these keys — one
  // resolver so the cell and its sorter always read the same value.
  const getStartedDateTime = record =>
    record.startedDateTime || record.startDate || record.assessmentStartedAt || record.startedAt

  // Helper function to render score with consistent styling
  const renderScore = text => (
    <div style={{ color: '#343E59', fontWeight: 'bold' }}>
      {typeof text === 'number'
        ? text === 0
          ? '0'
          : text.toFixed(2)
        : text || '-'}
    </div>
  )

  // Helper function to render percentage with consistent styling
  const renderPercentage = text => (
    <div style={{ color: '#343E59', fontWeight: 'bold' }}>
      {typeof text === 'number' ? `${text.toFixed(2)}%` : text || '-'}
    </div>
  )

  const renderIfCalculated = (renderer) => (text, record) =>
    record && record.scoresCalculated === false ? '-' : renderer(text, record)

  // Sort counterpart of renderIfCalculated: a row that renders "-" because its
  // scores are not calculated yet must sort as blank, never as a low score.
  const scoreIfCalculated = (getScore) => (record) =>
    record && record.scoresCalculated === false ? null : getScore(record)

  // Helper function to render proficiency level with appropriate styling
  const renderProficiency = level => {
    // Use green color for all proficiency levels except Beginner
    let color = '#0FB680'; // Default green color

    // Use red color specifically for Beginner
    if (level?.toLowerCase() === 'beginner') {
      color = '#F97066'; // Red for Beginner
    }

    return (
      <div style={{ color, fontWeight: 'bold' }}>
        {level || '-'}
      </div>
    );
  }

  // Helper function to render CEFR level with appropriate styling
  const renderCefrLevel = cefrLevel => {
    const bgColor = cefrLevel ? '#EBF8FF' : '#F3F4F6'
    const textColor = cefrLevel ? '#2563EB' : '#6B7280'

    return (
      <div
        style={{
          display: 'inline-block',
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: bgColor,
          color: textColor,
          fontWeight: 'bold',
          fontSize: '12px'
        }}
      >
        {cefrLevel ? cefrLevel.toUpperCase() : 'Not assigned'}
      </div>
    )
  }

  const onSearchRef = useRef(onSearch)
  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  const debounceTimerRef = useRef(null)
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  const handleSearch = e => {
    const value = e.target.value
    setSearchValue(value)
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      if (onSearchRef.current) {
        onSearchRef.current(value)
      }
    }, 500)
  }

  const handlePageChange = newPage => {
    onPageChange(newPage - 1)
  }

  const handleStudentClick = student => {
    setSelectedStudent(student)
    // A float opens on its first part; the tab strip inside the report swaps
    // between them without closing it.
    setActivePartId(isMixMatch ? (student?.parts || [])[0]?.mapId || null : null)
    setShowReportModal(true)
  }

  /* Each part's report is the per-type report it always was: the part's own
     scores merged onto the candidate's identity, rendered by the same
     component. Nothing about the individual reports changes.
     
     MEMOISED, and it has to be: the report drawer runs an effect keyed on the
     whole `student` object, so handing it a freshly built object on every
     render spins that effect — render, setState, render — into React's
     "maximum update depth exceeded". */
  const activePart = useMemo(
    () => (isMixMatch
      ? (selectedStudent?.parts || []).find(part => part.mapId === activePartId) || (selectedStudent?.parts || [])[0]
      : null),
    [isMixMatch, selectedStudent, activePartId]
  )
  const reportStudentForPart = useMemo(
    () => (activePart
      ? {
        ...selectedStudent,
        ...(activePart.report || {}),
        assessmentAssignedId: activePart.assessmentAssignedId,
        assessmentType: activePart.assessmentType,
      }
      : selectedStudent),
    [activePart, selectedStudent]
  )

  const handleExportData = () => {
    if (assessmentId) {
      return exportStudentData(assessmentId, entityType, statusType)
    }
    console.error('Assessment ID is required for export')
    return undefined
  }

  const handleSendReminders = async () => {
    if (!assessmentId) {
      console.error('Assessment ID is required to send reminders')
      return
    }

    // For pending status, check if any students are selected
    if (statusType === 'pending' && selectedStudentsForReminder.length === 0) {
      message.warning('Please select at least one student to send reminders')
      return
    }

    setSendingReminders(true)
    try {
      // Pass selected students if in pending status, otherwise send all
      const studentsToSendReminder = statusType === 'pending' ? selectedStudentsForReminder : null
      await sendRemindersToStudents(assessmentId, entityType, studentsToSendReminder)

      // Clear selected students after successful send
      if (statusType === 'pending') {
        setSelectedStudentsForReminder([])
      }

      message.success('Reminders sent successfully!')
    } catch (error) {
      message.error('Failed to send reminders')
    } finally {
      setSendingReminders(false)
    }
  }

  const handleResendInvites = async () => {
    if (!assessmentId) {
      console.error('Assessment ID is required to resend invites')
      return
    }

    // For droppedoff status, check if any students are selected
    if (statusType === 'droppedoff' && selectedStudentsForResend.length === 0) {
      message.warning('Please select at least one student to resend invites')
      return
    }

    setResendingInvites(true)
    try {
      // Pass selected students if in droppedoff status, otherwise send all
      const studentsToResend = statusType === 'droppedoff' ? selectedStudentsForResend : null
      await resendInvitesToStudents(assessmentId, entityType, studentsToResend)

      // Clear selected students after successful resend
      if (statusType === 'droppedoff') {
        setSelectedStudentsForResend([])
      }

      message.success('Invites resent successfully!')
    } catch (error) {
      message.error('Failed to resend invites')
    } finally {
      setResendingInvites(false)
    }
  }

  // Helper functions for student selection
  const handleStudentSelect = (studentEmail, checked) => {
    if (statusType === 'droppedoff') {
      if (checked) {
        setSelectedStudentsForResend([...selectedStudentsForResend, studentEmail])
      } else {
        setSelectedStudentsForResend(selectedStudentsForResend.filter(email => email !== studentEmail))
      }
    } else if (statusType === 'pending') {
      if (checked) {
        setSelectedStudentsForReminder([...selectedStudentsForReminder, studentEmail])
      } else {
        setSelectedStudentsForReminder(selectedStudentsForReminder.filter(email => email !== studentEmail))
      }
    }
  }

  const handleSelectAll = (checked) => {
    if (statusType === 'droppedoff') {
      if (checked) {
        const allStudentEmails = students.map(student => student.email)
        setSelectedStudentsForResend(allStudentEmails)
      } else {
        setSelectedStudentsForResend([])
      }
    } else if (statusType === 'pending') {
      if (checked) {
        const allStudentEmails = students.map(student => student.email)
        setSelectedStudentsForReminder(allStudentEmails)
      } else {
        setSelectedStudentsForReminder([])
      }
    }
  }

  const isAllSelected = students.length > 0 &&
    (statusType === 'droppedoff'
      ? students.every(student => selectedStudentsForResend.includes(student.email))
      : statusType === 'pending'
        ? students.every(student => selectedStudentsForReminder.includes(student.email))
        : false)

  const isSomeSelected =
    (statusType === 'droppedoff'
      ? selectedStudentsForResend.length > 0 && !isAllSelected
      : statusType === 'pending'
        ? selectedStudentsForReminder.length > 0 && !isAllSelected
        : false)

  // Define the base columns that are common to all status types
  const getBaseColumns = () => {
    let columns = [
      {
        title: 'STUDENT NAME',
        dataIndex: 'studentName',
        key: 'studentName',
        width: '16%',
        sorter: true,
        sortKind: SORT_KIND.TEXT,
        sortValue: record => record.studentName,
        render: (text, record) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#3249D7',
              fontWeight: 600,
            }}
            onClick={() => handleStudentClick(record)}
          >
            {record.student_img_url && (
              <Avatar
                src={record.student_img_url}
                alt={text}
                size={32}
                style={{ marginRight: '10px' }}
              />
            )}
            <div>
              <div>{text}</div>
              {record.studentNumber && (
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  {record.studentNumber}
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        title: 'CONTACT DETAILS',
        dataIndex: 'contactDetails',
        key: 'contactDetails',
        width: '16%',
        render: (_, record) => (
          <div>
            <div>{record.email && record.email !== 'undefined' ? record.email : ''}</div>
            <div>{record.phone && record.phone !== 'undefined' ? record.phone : ''}</div>
          </div>
        ),
      },
      {
        // Sits next to contact details because it answers a question about the
        // address on the left: did the invite actually get there?
        title: 'DELIVERY',
        dataIndex: 'deliveryStatus',
        key: 'deliveryStatus',
        width: '10%',
        render: (_, record) => (
          <DeliveryStatusTag
            status={record.deliveryStatus}
            label={record.deliveryStatusLabel}
            reason={record.deliveryStatusReason}
            channels={record.deliveryChannels}
          />
        ),
      }
    ]

    // Only add ASSIGNED CEFR column for communication assessments
    if (assessmentType && assessmentType.toLowerCase() === 'communication') {
      columns.push({
        title: 'ASSIGNED CEFR',
        dataIndex: 'assignedcefr',
        key: 'assignedcefr',
        width: '12%',
        sorter: true,
        // A1 < A2 < B1 … C2 orders correctly as plain text.
        sortKind: SORT_KIND.TEXT,
        sortValue: record => record.assignedcefr,
        render: (text, record) => renderCefrLevel(record.assignedcefr),
      })
    }

    return columns
  }

  // Define status-specific columns
  const getStatusColumns = () => {
    const baseColumns = getBaseColumns()

    switch (statusType) {
      case 'sent':
        return [
          ...baseColumns,
          {
            title: 'ASSESSMENT SENT DATE',
            dataIndex: 'sentDate',
            key: 'sentDate',
            width: '16%',
            sorter: true,
            sortKind: SORT_KIND.DATE,
            sortValue: record => record.sentDate,
            render: text => formatDateOnly(text),
          },
          {
            title: 'ASSMT. START & END DATE',
            key: 'assessmentDates',
            width: '16%',
            sorter: true,
            // Sorts on the start date the cell shows (falls back to sent date).
            sortKind: SORT_KIND.DATE,
            sortValue: record => record.startDate || record.sentDate,
            render: (_, record) => {
              const start = formatDateOnly(record.startDate);
              const effectiveStart = start === '' ? formatDateOnly(record.sentDate) : start;
              const end = formatDateOnly(record.endDate) || '';
              return (
                <div>
                  {effectiveStart}
                  {record.startDate && record.endDate ? ' - ' : ''}
                  {end}
                </div>
              );
            },
          },
        ]

      case 'pending':
        return [
          {
            title: (
              <Checkbox
                checked={isAllSelected}
                indeterminate={isSomeSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            ),
            key: 'select',
            width: '3%',
            render: (_, record) => (
              <Checkbox
                checked={selectedStudentsForReminder.includes(record.email)}
                onChange={(e) => handleStudentSelect(record.email, e.target.checked)}
              />
            ),
          },
          ...baseColumns,
          {
            title: 'ASSESSMENT SENT DATE',
            dataIndex: 'sentDate',
            key: 'sentDate',
            width: '16%',
            sorter: true,
            sortKind: SORT_KIND.DATE,
            sortValue: record => record.sentDate,
            render: text => formatDateOnly(text),
          },
          {
            title: 'ASSMT. START & END DATE',
            key: 'assessmentDates',
            width: '16%',
            sorter: true,
            // Sorts on the start date the cell shows (falls back to sent date).
            sortKind: SORT_KIND.DATE,
            sortValue: record => record.startDate || record.sentDate,
            render: (_, record) => {
              const start = formatDateOnly(record.startDate);
              const effectiveStart = start === '' ? formatDateOnly(record.sentDate) : start;
              const end = formatDateOnly(record.endDate) || '';
              return (
                <div>
                  {effectiveStart}
                  {record.startDate && record.endDate ? ' - ' : ''}
                  {end}
                </div>
              );
            },
          },
        ]

      case 'inprogress':
        return [
          ...baseColumns,
          {
            title: 'ASSESSMENT SENT DATE',
            dataIndex: 'sentDate',
            key: 'sentDate',
            width: '16%',
            sorter: true,
            sortKind: SORT_KIND.DATE,
            sortValue: record => record.sentDate,
            render: text => formatDateOnly(text),
          },
          {
            title: 'ASSMT. STARTED DATE & TIME',
            dataIndex: 'startedDateTime',
            key: 'startedDateTime',
            width: '16%',
            sorter: true,
            sortKind: SORT_KIND.DATE,
            sortValue: getStartedDateTime,
            render: (text, record) => formatEventDateOnly(getStartedDateTime(record)),
          }
        ]

      case 'droppedoff':
        return [
          {
            title: (
              <Checkbox
                checked={isAllSelected}
                indeterminate={isSomeSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            ),
            key: 'select',
            width: '3%',
            render: (_, record) => (
              <Checkbox
                checked={selectedStudentsForResend.includes(record.email)}
                onChange={(e) => handleStudentSelect(record.email, e.target.checked)}
              />
            ),
          },
          ...baseColumns,
          {
            title: 'ASSESSMENT SENT DATE',
            dataIndex: 'sentDate',
            key: 'sentDate',
            width: '16%',
            sorter: true,
            sortKind: SORT_KIND.DATE,
            sortValue: record => record.sentDate,
            render: text => formatDateOnly(text),
          },
          {
            title: 'ASSMT. DROPPED OFF DATE & TIME',
            dataIndex: 'droppedOffDateTime',
            key: 'droppedOffDateTime',
            width: '16%',
            sorter: true,
            sortKind: SORT_KIND.DATE,
            sortValue: record => record.droppedOffDateTime,
            // Candidates who dropped off before the backend started recording
            // dropped_at have no timestamp — say so instead of leaving the cell
            // blank (it previously showed the assessment deadline for everyone).
            render: text => formatEventDateOnly(text) || '-',
          },
        ]

      case 'completed': {
        let cols = [
          ...baseColumns.map(col => ({ ...col, width: col.width || 180 })),
          {
            title: 'ASSMT. SENT DATE',
            dataIndex: 'sentDate',
            key: 'sentDate',
            width: 180,
            sorter: true,
            sortKind: SORT_KIND.DATE,
            sortValue: record => record.sentDate,
            render: text => formatDateOnly(text),
          },
          {
            title: 'ASSMT. STARTED DATE & TIME',
            dataIndex: 'startedDateTime',
            key: 'startedDateTime',
            width: 200,
            sorter: true,
            sortKind: SORT_KIND.DATE,
            sortValue: record => record.startedDateTime,
            render: text => formatEventDateOnly(text),
          }
        ]

        /* Mix & Match: a row is a candidate, not a candidate-in-one-assessment.
           The final score leads, because that is the float's result; the parts
           it is made of follow, each showing the score its own report shows. */
        if (isMixMatch) {
          cols.push({
            title: 'FINAL SCORE',
            dataIndex: 'overallScore',
            key: 'overallScore',
            width: 150,
            sorter: true,
            sortKind: SORT_KIND.NUMBER,
            sortValue: record => (typeof record.overallScore === 'number' ? record.overallScore : -1),
            render: (_, record) => (
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
                  {formatPartScore(record.overallScore)}
                </div>
                <div style={{ fontSize: 12, color: COLORS.GREY_T_60 }}>
                  {record.partsScored || 0} of {record.partsTotal || 0} scored
                </div>
              </div>
            ),
          })
          mixMatchParts.forEach(part => {
            cols.push({
              title: partLabel(part.typeName).toUpperCase(),
              key: `part-${part.mapId}`,
              width: 160,
              sorter: true,
              sortKind: SORT_KIND.NUMBER,
              sortValue: record => {
                const taken = (record.parts || []).find(item => item.mapId === part.mapId)
                return typeof taken?.score === 'number' ? taken.score : -1
              },
              render: (_, record) => {
                const taken = (record.parts || []).find(item => item.mapId === part.mapId)
                if (!taken) return '-'
                return (
                  <div>
                    <div style={{ fontWeight: 600 }}>{formatPartScore(taken.score)}</div>
                    {taken.submitted && taken.score === null ? (
                      <Tooltip title={ATTEMPT_STATUS_TOOLTIP.reportPreparing}>
                        <Tag color="processing" style={{ marginTop: 4 }}>{ATTEMPT_STATUS.reportPreparing.label}</Tag>
                      </Tooltip>
                    ) : (
                      <Tag color={PART_STATUS_COLORS[taken.status] || 'default'} style={{ marginTop: 4 }}>
                        {ATTEMPT_STATUS[toAttemptStatusKey(taken.status)]?.label || taken.status || '-'}
                      </Tag>
                    )}
                  </div>
                )
              },
            })
          })
        }

        // Add ACHIEVED CEFR column only for communication assessments
        if (assessmentType && assessmentType.toLowerCase() === 'communication') {
          cols.push({
            title: 'ACHIEVED CEFR',
            dataIndex: 'cefrLevel',
            key: 'cefrLevel',
            width: '12%',
            sorter: true,
            sortKind: SORT_KIND.TEXT,
            sortValue: record => record.cefrLevel,
            render: (_, record) => renderCefrLevel(record.cefrLevel),
          })
        }

        // Add assessment-specific columns based on assessment type
        if (assessmentType && assessmentType.toLowerCase() === 'behavior') {
          // Extract all unique behavior proficiency keys from all students
          const behaviorKeys = new Set()
          students.forEach(student => {
            if (student.behaviorProficiencies) {
              Object.keys(student.behaviorProficiencies).forEach(key => {
                behaviorKeys.add(key)
              })
            }
          })

          // Convert to array and sort alphabetically
          const sortedBehaviorKeys = Array.from(behaviorKeys).sort()

          // Add a column for each behavior proficiency key
          sortedBehaviorKeys.forEach(key => {
            const capitalizedKey = key.toUpperCase()

            cols.push({
              title: capitalizedKey,
              key: key.replace(/\s+/g, '').toLowerCase(),
              width: 150,
              sorter: true,
              // Rank the proficiency ladder, not the label — "Beginner" would
              // otherwise sort ahead of "Practitioner" alphabetically.
              sortKind: SORT_KIND.NUMBER,
              sortValue: record => proficiencyRank(record.behaviorProficiencies?.[key]),
              render: (_, record) => renderProficiency(record.behaviorProficiencies?.[key]),
            })
          })

          // If no behavior keys were found, add a message
          if (sortedBehaviorKeys.length === 0) {
            cols.push({
              title: 'BEHAVIOR PROFICIENCIES',
              key: 'noProficiencies',
              width: 150,
              render: () => <div>No behavior data available</div>,
            })
          }
        } else if (assessmentType && assessmentType.toLowerCase() === 'aptitude') {
          // Add aptitude-specific columns. Only the sections this assessment
          // actually has get a column — an unselected section carries no score
          // (admin-node sends "-"), so showing it is a dead column.
          cols.push({
            title: (
              <div>
                OVERALL
                <br />
                PERCENTAGE
              </div>
            ),
            key: 'overallPercentage',
            width: 150,
            sorter: true,
            sortKind: SORT_KIND.NUMBER,
            sortValue: record => record.aptitudeScores?.overallPercentage,
            render: (_, record) => renderPercentage(record.aptitudeScores?.overallPercentage),
          })

          resolveAptitudeSectionKeys(students).forEach(key => {
            cols.push({
              title: (
                <div>
                  {APTITUDE_SECTION_LABELS[key].toUpperCase()}
                  <br />
                  PERCENTAGE
                </div>
              ),
              key,
              width: 180,
              sorter: true,
              sortKind: SORT_KIND.NUMBER,
              sortValue: record => record.aptitudeScores?.[key],
              render: (_, record) => renderPercentage(record.aptitudeScores?.[key]),
            })
          })
        } else if (assessmentType && assessmentType.toLowerCase() === 'communication') {
          // Add communication assessment specific columns
          cols.push(
            {
              title: (
                <div>
                  TOTAL SCORE
                </div>
              ),
              dataIndex: 'totalScore',
              key: 'totalScore',
              width: 150,
              sorter: true,
              sortKind: SORT_KIND.NUMBER,
              sortValue: scoreIfCalculated(record => record.totalScore),
              render: renderIfCalculated((text) => renderScore(text)),
            },
            {
              title: (
                <div>
                  SPEAKING ABILITY
                </div>
              ),
              dataIndex: 'englishVerbalScore',
              key: 'englishVerbalScore',
              width: 150,
              sorter: true,
              sortKind: SORT_KIND.NUMBER,
              sortValue: scoreIfCalculated(record => record.englishVerbalScore),
              render: renderIfCalculated((text) => renderScore(text)),
            },
            {
              title: (
                <div>
                  READING ABILITY
                </div>
              ),
              dataIndex: 'readingAbilityScore',
              key: 'readingAbilityScore',
              width: 150,
              sorter: true,
              sortKind: SORT_KIND.NUMBER,
              sortValue: scoreIfCalculated(record => record.readingAbilityScore),
              render: renderIfCalculated((text) => renderScore(text)),
            },
            {
              title: (
                <div>
                  LISTENING ABILITY
                </div>
              ),
              dataIndex: 'listeningAbilityScore',
              key: 'listeningAbilityScore',
              width: 150,
              sorter: true,
              sortKind: SORT_KIND.NUMBER,
              sortValue: scoreIfCalculated(record => record.listeningAbilityScore),
              render: renderIfCalculated((text) => renderScore(text)),
            },
            {
              title: (
                <div>
                  WRITING ABILITY
                </div>
              ),
              dataIndex: 'writingScore',
              key: 'writingScore',
              width: 150,
              sorter: true,
              sortKind: SORT_KIND.NUMBER,
              sortValue: scoreIfCalculated(record => record.writingScore),
              render: renderIfCalculated((text) => renderScore(text)),
            }
          )
        } else if (assessmentType && ['role-based', 'role_based', 'rolebased', 'role based'].includes(assessmentType.toLowerCase())) {
          // Role-based columns = the SELECTED sections (question count > 0 at
          // creation), driven by `roleBasedSections` from the API so they appear
          // even before scores are calculated. Falls back to the score keys
          // present in the rows if the API hasn't supplied the section list.
          const roleBasedRender = (scoreKey) => (_, record) => {
            if (record && record.scoresCalculated === false) return '-'
            return renderScore(record.roleBasedScores?.[scoreKey])
          }

          const roleBasedSortValue = (scoreKey) =>
            scoreIfCalculated((record) => record.roleBasedScores?.[scoreKey])

          const sectionDefs = resolveRoleBasedSectionColumns(roleBasedSections, students)

          const twoLineTitle = (label) => (
            <div>
              {String(label || '').toUpperCase()}
              <br />
              SCORE
            </div>
          )

          cols.push({
            title: twoLineTitle('OVERALL'),
            key: 'overallScore',
            width: 150,
            sorter: true,
            sortKind: SORT_KIND.NUMBER,
            sortValue: roleBasedSortValue('overallScore'),
            render: roleBasedRender('overallScore'),
          })
          sectionDefs.forEach((def) => {
            cols.push({
              title: twoLineTitle(def.label),
              key: def.key,
              width: 150,
              sorter: true,
              sortKind: SORT_KIND.NUMBER,
              sortValue: roleBasedSortValue(def.key),
              render: roleBasedRender(def.key),
            })
          })
        }
        const normalizedAssessmentType = assessmentType
          ? assessmentType.toLowerCase().replace(/[\s-]/g, '')
          : ''

        if (
          allowProctoring &&
          ['communication', 'aptitude', 'rolebased', 'custom'].includes(normalizedAssessmentType)
        ) {
          cols.push({
            title: 'PROCTORING STATUS',
            dataIndex: 'proctoringStatus',
            key: 'proctoringStatus',
            width: 180,
            sorter: true,
            // Rank Good → Bad → Evaluating; getProctoringLabel is shared with the
            // renderer below so the sorted value is always the shown value.
            sortKind: SORT_KIND.NUMBER,
            sortValue: proctoringRank,
            render: (_, record) => {
              const label = getProctoringLabel(record)
              if (!label) return '-'
              return (
                <div
                  style={{
                    color: PROCTORING_COLORS[label],
                    fontWeight: 'bold',
                  }}
                >
                  {label}
                </div>
              )
            },
          })
        }
        return cols
      }

      default:
        return baseColumns
    }
  }

  // Get the appropriate columns based on status type
  const baseStatusColumns = getStatusColumns()

  // Append a per-row "Copy Link" action so admins can share the invite link
  // manually when email delivery fails. Not shown on the "completed" tab (the
  // candidate has already finished — no link to share). The link itself is
  // fetched on click; corporate → OTP invite link, institute → activation link.
  const statusColumns =
    statusType === 'completed'
      ? baseStatusColumns
      : [
          ...baseStatusColumns,
          {
            title: 'ACTIONS',
            key: 'copyLink',
            width: '10%',
            render: (_, record) => (
              <CopyLinkButton record={record} entityType={entityType} />
            ),
          },
        ]

  // Drive the arrow off our own state so exactly one column reads as sorted, and
  // reuse the shared "Click to sort …" tooltip the other admin tables use.
  const columns = statusColumns.map(column => {
    if (!column.sorter) {
      return column
    }
    const order = sorter.key === column.key ? sorter.order : null
    return {
      ...column,
      sortOrder: order,
      showSorterTooltip: fixedSorterColumn(SORTER_TOOLTIP_ORDER[order]),
    }
  })

  // Switching attempt tabs keeps the sort when the new tab has that column
  // (name, sent date) and quietly drops it when it does not — the arrow only
  // renders on a column that exists, so there is nothing stale on screen.
  const activeSortColumn = sorter.order
    ? columns.find(column => column.key === sorter.key)
    : null

  // Sort the WHOLE list, then slice — antd only ever receives the current page,
  // so letting it sort would reorder ten rows instead of all of them.
  const sortedStudents = useMemo(
    () => sortRows(students, activeSortColumn, sorter.order),
    // activeSortColumn is rebuilt every render, so an active sort re-runs on each
    // render. That is a few ms on the largest assessment and keeps the accessors
    // free of stale closures; when no sort is active this memo is stable.
    [students, activeSortColumn, sorter.order]
  )

  const paginatedStudents = useMemo(() => {
    const startIndex = page * limit
    return sortedStudents.slice(startIndex, startIndex + limit)
  }, [sortedStudents, page, limit])

  // Sorting reorders every page, so the page the admin is looking at no longer
  // means anything — send them back to the first page of the new order.
  const handleTableChange = (pagination, filters, nextSorter) => {
    const order = nextSorter?.order || null
    setSorter({ key: order ? nextSorter.columnKey : null, order })
    onPageChange(0)
  }

  return (
    <>
      <div style={{ marginTop: '20px' }}>
        <TableTop>
          <SearchSection>
            <Input
              placeholder={statusType === 'sent' ? 'Search by name or email' : 'Search by student name'}
              prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
              suffix={loading ? <Spin size="small" /> : null}
              value={searchValue}
              onChange={handleSearch}
            />
          </SearchSection>

          {/* <FilterContainer>
            <FilterButtonComponent onClick={() => setShowFilters(!showFilters)}>
              <FilterIcon />
              <span>Filter</span>
              <ArrowDropDown />
            </FilterButtonComponent>
            {showFilters && (
              <>
                <FilterPopup></FilterPopup>
                <Overlay onClick={() => setShowFilters(false)} />
              </>
            )}
          </FilterContainer> */}

          <ShowingText>
            Showing {Math.min(students.length + 10 * page)} of {totalItems}
          </ShowingText>

          {statusType === 'pending' && (
            <Button
              onClick={handleSendReminders}
              loading={sendingReminders}
              style={{
                height: '48px',
                fontFamily: 'Plus Jakarta Sans',
                fontWeight: 600,
                fontSize: '14px',
                background: '#FBFAFF',
                border: '1px solid #E3DEFF',
                boxShadow: '0px 1px 2px 0px #1018280D',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                verticalAlign: 'middle',
                color: '#3249D7',
                marginTop: '10px',
              }}
            >
              {statusType === 'pending' && selectedStudentsForReminder.length > 0
                ? `Send Reminders (${selectedStudentsForReminder.length})`
                : 'Send Reminders'}
            </Button>
          )}

          {statusType === 'droppedoff' && (
            <Button
              onClick={handleResendInvites}
              loading={resendingInvites}
              style={{
                fontFamily: 'Plus Jakarta Sans',
                height: '48px',
                fontWeight: 600,
                fontSize: '14px',
                background: '#FBFAFF',
                border: '1px solid #E3DEFF',
                boxShadow: '0px 1px 2px 0px #1018280D',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                verticalAlign: 'middle',
                color: '#3249D7',
                marginTop: '10px',
              }}
            >
              {statusType === 'droppedoff' && selectedStudentsForResend.length > 0
                ? `Resend Invites (${selectedStudentsForResend.length})`
                : 'Resend Invites'}
            </Button>
          )}

          {(statusType === 'completed' || statusType === 'sent') && (
            <DownloadButton
              onClick={handleExportData}
              style={{
                height: '48px',
                fontFamily: 'Plus Jakarta Sans',
                fontWeight: 600,
                fontSize: '14px',
                background: '#FBFAFF',
                border: '1px solid #E3DEFF',
                boxShadow: '0px 1px 2px 0px #1018280D',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                verticalAlign: 'middle',
                color: '#0FB680',
                marginTop: '10px',
              }}
            >
              Export
            </DownloadButton>
          )}
        </TableTop>

        <div style={{ margin: '10px' }}> </div>

        {/* Match the Spin and Table structure */}
        <Spin spinning={loading}>
          {students && students.length > 0 ? (
            <CustomStyledTable
              columns={columns}
              dataSource={paginatedStudents}
              pagination={false}
              onChange={handleTableChange}
              rowKey="id"
              scroll={{
                x:
                  assessmentType &&
                    assessmentType.toLowerCase() === 'behavior' &&
                    statusType === 'completed'
                    ? 2000 // Increased width for behavior assessment with many columns
                    : assessmentType &&
                      assessmentType.toLowerCase() === 'aptitude' &&
                      statusType === 'completed'
                      ? 1800 // Increased width for aptitude assessment
                      : statusType === 'completed'
                        ? 1600
                        : statusType === 'inprogress'
                          ? 1200
                          : 1000,
              }}
              size="middle"
              bordered={false}
              rowClassName={() => 'table-row'}
            />
          ) : (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <Empty description="No student data available" />
            </div>
          )}
        </Spin>

        {/* Match the pagination container structure */}
        {totalItems > limit && (
          <div
            style={{
              margin: '16px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Pagination
              current={parseInt(page) + 1}
              total={totalItems}
              pageSize={parseInt(limit)}
              onChange={handlePageChange}
              // Make sure to use the same pagination style
              size="default"
              showSizeChanger={false}
            />
          </div>
        )}
      </div>

      <StudentReportModal
        visible={showReportModal}
        onClose={() => setShowReportModal(false)}
        student={isMixMatch ? reportStudentForPart : selectedStudent}
        allowProctoring={allowProctoring}
        isProctoringDisabled={allowProctoring === false}
        assessmentMapId={isMixMatch ? activePart?.mapId : assessmentId}
        assessmentType={isMixMatch ? activePart?.assessmentType : assessmentType}
        headerExtra={
          isMixMatch && selectedStudent ? (
            <div style={{ padding: '12px 24px 0', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: '#6B7280' }}>
                  FINAL SCORE
                </span>
                <b style={{ fontSize: 22 }}>{formatPartScore(selectedStudent.overallScore)}</b>
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  average of {selectedStudent.partsScored || 0} of {selectedStudent.partsTotal || 0} assessments scored
                </span>
              </div>
              <Tabs
                activeKey={activePartId || undefined}
                onChange={setActivePartId}
                items={(selectedStudent.parts || []).map(part => ({
                  key: part.mapId,
                  label: `${partLabel(part.assessmentType)} · ${formatPartScore(part.score)}`,
                }))}
              />
            </div>
          ) : null
        }
      />
    </>
  )
}

export default StudentsTable
